/* facets.js — simplified for unified pill UI (no mobile drawer) */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 300);

    this.form = this.querySelector('#FacetFiltersForm');
    if (this.form) this.form.addEventListener('input', this.debouncedOnSubmit);

    // Handle Enter on price fields (so it doesn't full-submit the page)
    this.form?.querySelectorAll('input[type="number"], input[type="text"]').forEach((el) => {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
    });
  }

  /* ---------- History listener ---------- */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /* ---------- Small helpers ---------- */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((el) => {
      el.classList.toggle('disabled', disable);
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const fd = new FormData(form);
    return new URLSearchParams(fd).toString();
  }

  /* ---------- Core render flow ---------- */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countMobile = document.getElementById('ProductCount');
    const countDesktop = document.getElementById('ProductCountDesktop');

    // Loading states
    const grid = document.getElementById('ProductGridContainer');
    grid?.querySelector('.collection')?.classList.add('loading');
    countMobile?.classList.add('loading');
    countDesktop?.classList.add('loading');
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((s) => s.classList.remove('hidden'));

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const match = (e) => e.url === url;

      FacetFiltersForm.filterData.some(match)
        ? FacetFiltersForm.renderSectionFromCache(match)
        : FacetFiltersForm.renderSectionFromFetch(url);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url) {
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.applyHTML(html);
      });
  }

  static renderSectionFromCache(matchFn) {
    const html = FacetFiltersForm.filterData.find(matchFn).html;
    FacetFiltersForm.applyHTML(html);
  }

  /* ---------- DOM patching for your simplified markup ---------- */
  static applyHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // 1) Product grid
    const newGrid = doc.getElementById('ProductGridContainer');
    const curGrid = document.getElementById('ProductGridContainer');
    if (newGrid && curGrid) {
      curGrid.innerHTML = newGrid.innerHTML;
      curGrid.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
    }

    // 2) Counts
    const newCount = doc.getElementById('ProductCount')?.innerHTML;
    const curCount = document.getElementById('ProductCount');
    const curCountDesktop = document.getElementById('ProductCountDesktop');
    if (newCount != null && curCount) {
      curCount.innerHTML = newCount;
      curCount.classList.remove('loading');
      if (curCountDesktop) {
        curCountDesktop.innerHTML = newCount;
        curCountDesktop.classList.remove('loading');
      }
    }

    // 3) Active chips (both desktop+mobile share the same block in your markup)
    ['.active-facets'].forEach((sel) => {
      const newEl = doc.querySelector(sel);
      const curEl = document.querySelector(sel);
      if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
    });

    // 4) Facet form UI (keep the same <form> element so its listener remains)
    const newForm = doc.querySelector('#FacetFiltersForm');
    const curForm = document.querySelector('#FacetFiltersForm');
    if (newForm && curForm) {
      curForm.innerHTML = newForm.innerHTML;

      // Re-bind tiny things that attach to *children* (the form-level input listener persists)
      curForm.querySelectorAll('input[type="number"], input[type="text"]').forEach((el) => {
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') e.preventDefault();
        });
      });
    }

    // Hide spinners & loading
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((s) => s.classList.add('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
  }

  /* ---------- Counts & URL ---------- */
  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`);
  }

  /* ---------- Events ---------- */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // Single unified form: always read params from #FacetFiltersForm
    const form = this.form || event.target.closest('form');
    const params = this.createSearchParams(form);
    this.onSubmitForm(params, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.includes('?')
      ? event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1)
      : '';
    FacetFiltersForm.renderPage(url);
  }
}

/* ---------- Boot ---------- */
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ---------- Price range (unchanged, but kept minimal) ---------- */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((el) => {
      el.addEventListener('change', this.onRangeChange.bind(this));
      el.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }
  onRangeChange(e) {
    this.adjustToValidValues(e.currentTarget);
    this.setMinAndMaxValues();
  }
  onKeyDown(e) {
    if (e.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!e.key.match(pattern)) e.preventDefault();
  }
  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

/* ---------- facet-remove (unchanged) ---------- */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (e) => {
      e.preventDefault();
      if (e.code.toUpperCase() === 'SPACE') this.closeFilter(e);
    });
  }
  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);

/* ---------- tiny debounce util (Dawn has one; include if needed) ---------- */
function debounce(fn, wait = 250) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
